import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAppStore } from "@/core/store/useAppStore";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Modal } from "@/components/ui/Modal";
import { PlusIcon, SpinnerIcon, TrashIcon } from "@/components/icons";
import {
  clientEmailsService,
  type ClientEmail,
  type ClientEmailRole,
} from "../clientEmails.service";
import { assignablePeopleKeys } from "@/domains/Tasks/types";
import { type AgencyPerson } from "../agencyPeople.service";
import { agencyPeopleKeys, findPersonByEmail, nameToSend, teamPersonIds } from "../agencyPeople";
import { saveAccessEdit, type AccessEdit } from "../accessEdit";
import { NAME_LOCKED_MESSAGE, NAME_MAX_LENGTH, renameErrorMessage } from "../personName";
import { useAgencyPeopleQuery, useRenamePersonMutation } from "../useAgencyPeople";

/**
 * Admin UI for managing the portal access emails attached to a client.
 *
 * Each email can receive a magic link to sign in to the client portal,
 * or — when ``role === 'creator'`` — to the on-site producer app at
 * ``/creator``. Adding an email triggers a welcome magic link from the
 * backend; removing the last email revokes existing tokens server-side.
 *
 * Copy is in Italian to match the rest of the magic-link auth UI.
 */

interface ClientEmailsSectionProps {
  clientId: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const NO_PEOPLE: AgencyPerson[] = [];

interface AxiosLikeError {
  response?: { status?: number };
  message?: string;
}

const getStatus = (err: unknown): number | undefined => {
  if (err && typeof err === "object" && "response" in err) {
    return (err as AxiosLikeError).response?.status;
  }
  return undefined;
};

const formatCreatedAt = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
};

interface RolePillStyle {
  bg: string;
  text: string;
  label: string;
}

const ROLE_STYLES: Record<ClientEmailRole, RolePillStyle> = {
  client: { bg: "bg-gray-100", text: "text-gray-700", label: "Cliente" },
  creator: { bg: "bg-indigo-100", text: "text-indigo-700", label: "Creator" },
};

const RolePill: React.FC<{ role: ClientEmailRole }> = ({ role }) => {
  const style = ROLE_STYLES[role];
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}
    >
      {style.label}
    </span>
  );
};

type ModalMode =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; email: ClientEmail };

interface EmailModalProps {
  mode: ModalMode;
  isSubmitting: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onCreate: (input: {
    email: string;
    name: string | undefined;
    role: ClientEmailRole;
  }) => Promise<void>;
  onSaveEdit: (email: ClientEmail, edit: AccessEdit) => Promise<void>;
  people: AgencyPerson[];
}

/**
 * Modal used both to create a new access email and to edit an existing
 * one. In edit mode the email is fixed; the name (global, TD-086) and the
 * role can change.
 */
const EmailModal: React.FC<EmailModalProps> = ({
  mode,
  isSubmitting,
  errorMessage,
  onClose,
  onCreate,
  onSaveEdit,
  people,
}) => {
  const isOpen = mode.kind !== "closed";
  const isEdit = mode.kind === "edit";

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [role, setRole] = useState<ClientEmailRole>("client");
  const [localError, setLocalError] = useState<string | null>(null);
  const knownPerson = useMemo(() => findPersonByEmail(people, email), [people, email]);

  useEffect(() => {
    if (mode.kind === "create" && !nameTouched) {
      setName(knownPerson?.name ?? "");
    }
  }, [mode.kind, knownPerson, nameTouched]);

  // Reset form whenever the modal opens or switches mode.
  useEffect(() => {
    if (mode.kind === "create") {
      setEmail("");
      setName("");
      setNameTouched(false);
      setRole("client");
      setLocalError(null);
    } else if (mode.kind === "edit") {
      setEmail(mode.email.email);
      setName(mode.email.name ?? "");
      setRole(mode.email.role);
      setLocalError(null);
    }
  }, [mode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (mode.kind === "edit") {
      await onSaveEdit(mode.email, { name, role });
      return;
    }

    if (mode.kind === "create") {
      const normalized = email.trim().toLowerCase();
      if (!EMAIL_REGEX.test(normalized)) {
        setLocalError("Inserisci un indirizzo email valido.");
        return;
      }
      await onCreate({
        email: normalized,
        name: nameToSend(name, nameTouched, knownPerson),
        role,
      });
    }
  };

  const title = isEdit ? "Modifica accesso" : "Aggiungi email";
  const submitLabel = isEdit ? "Salva" : "Invia invito";
  const visibleError = errorMessage ?? localError;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!isSubmitting) onClose();
      }}
      title={title}
      size="md"
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        {!isEdit && (
          <>
            <div className="space-y-1">
              <label
                htmlFor="client-email-input"
                className="text-sm font-medium text-gray-900"
              >
                Email
              </label>
              <input
                id="client-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="persona@esempio.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden text-sm"
                disabled={isSubmitting}
                required
                autoFocus
              />
            </div>

            <div className="space-y-1">
              <label
                htmlFor="client-email-name-input"
                className="text-sm font-medium text-gray-900"
              >
                Nome <span className="text-gray-400">(opzionale)</span>
              </label>
              <input
                id="client-email-name-input"
                type="text"
                value={name}
                onChange={(e) => {
                  setNameTouched(true);
                  setName(e.target.value);
                }}
                placeholder="es. Marco Rossi"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden text-sm"
                disabled={isSubmitting}
              />
              {knownPerson && (
                <p className="text-xs text-gray-500">
                  Persona già registrata: il nome vale per tutti i clienti.
                </p>
              )}
            </div>
          </>
        )}

        {mode.kind === "edit" && (
          <>
            <p className="text-sm font-medium text-gray-900">{email}</p>
            <div className="space-y-1">
              <label
                htmlFor="client-email-edit-name-input"
                className="text-sm font-medium text-gray-900"
              >
                Nome
              </label>
              <input
                id="client-email-edit-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={NAME_MAX_LENGTH}
                placeholder="es. Marco Rossi"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden text-sm"
                disabled={isSubmitting || mode.email.personId === null}
              />
              <p className="text-xs text-gray-500">
                Il nome vale per tutti i clienti.
              </p>
            </div>
          </>
        )}

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-gray-900">Ruolo</legend>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name="client-email-role"
              value="client"
              checked={role === "client"}
              onChange={() => setRole("client")}
              disabled={isSubmitting}
              className="mt-0.5"
            />
            <span className="text-sm">
              <span className="font-medium text-gray-900">Cliente</span>
              <span className="block text-xs text-gray-500">
                Accede al portale di approvazione e revisione contenuti.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name="client-email-role"
              value="creator"
              checked={role === "creator"}
              onChange={() => setRole("creator")}
              disabled={isSubmitting}
              className="mt-0.5"
            />
            <span className="text-sm">
              <span className="font-medium text-gray-900">Creator</span>
              <span className="block text-xs text-gray-500">
                Accede all'app on-site per fotografi e video editor.
              </span>
            </span>
          </label>
        </fieldset>

        {visibleError && (
          <p className="text-xs text-red-600">{visibleError}</p>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <SpinnerIcon className="w-4 h-4" />
                Invio...
              </>
            ) : (
              submitLabel
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export const ClientEmailsSection: React.FC<ClientEmailsSectionProps> = ({
  clientId,
}) => {
  const addToast = useAppStore((state) => state.addToast);
  const queryClient = useQueryClient();

  const { data: people = NO_PEOPLE } = useAgencyPeopleQuery();
  const renamePerson = useRenamePersonMutation();
  const teamIds = useMemo(() => teamPersonIds(people), [people]);

  const invalidateAssignees = () =>
    queryClient.invalidateQueries({ queryKey: assignablePeopleKeys.client(clientId) });

  const [emails, setEmails] = useState<ClientEmail[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [modalMode, setModalMode] = useState<ModalMode>({ kind: "closed" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [pendingRemoval, setPendingRemoval] = useState<ClientEmail | null>(
    null,
  );
  const [isRemoving, setIsRemoving] = useState(false);

  const loadEmails = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const rows = await clientEmailsService.list(clientId);
      setEmails(rows);
    } catch (err) {
      setLoadError(
        err instanceof Error
          ? err.message
          : "Impossibile caricare le email di accesso.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    void loadEmails();
  }, [loadEmails]);

  const closeModal = () => {
    if (isSubmitting) return;
    setModalMode({ kind: "closed" });
    setSubmitError(null);
  };

  const handleCreate = async (input: {
    email: string;
    name: string | undefined;
    role: ClientEmailRole;
  }) => {
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const result = await clientEmailsService.add(clientId, {
        email: input.email,
        name: input.name,
        role: input.role,
      });
      setEmails((prev) => [...prev, result.email]);
      void queryClient.invalidateQueries({ queryKey: agencyPeopleKeys.all });
      void invalidateAssignees();
      addToast({
        message: result.emailSent
          ? `Email di benvenuto inviata a ${result.email.email}`
          : `Email aggiunta (invio email di benvenuto fallito)`,
        type: result.emailSent ? "success" : "warning",
        duration: 4000,
      });
      // Strict check: a backend without the field must not warn.
      if (result.nameApplied === false) {
        addToast({ message: NAME_LOCKED_MESSAGE, type: "warning", duration: 5000 });
      }
      setModalMode({ kind: "closed" });
    } catch (err) {
      const status = getStatus(err);
      if (status === 409) {
        const msg = "Questa email è già associata a questo cliente.";
        setSubmitError(msg);
        addToast({ message: msg, type: "warning", duration: 4000 });
      } else {
        const msg =
          err instanceof Error
            ? err.message
            : "Impossibile aggiungere l'email.";
        setSubmitError(msg);
        addToast({ message: msg, type: "error", duration: 5000 });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveEdit = async (opened: ClientEmail, edit: AccessEdit) => {
    // The modal holds the row as it was opened. After a partial save (name
    // saved, role not) the list holds the saved name, which is the baseline.
    const target = emails.find((row) => row.id === opened.id) ?? opened;
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const saved = await saveAccessEdit(target, edit, {
        rename: async (personId, name) => {
          const person = await renamePerson.mutateAsync({ personId, name });
          setEmails((prev) =>
            prev.map((row) =>
              row.personId === person.id ? { ...row, name: person.name } : row,
            ),
          );
        },
        updateRole: async (role) => {
          const result = await clientEmailsService.updateRole(clientId, target.id, role);
          setEmails((prev) =>
            prev.map((row) => (row.id === target.id ? result.email : row)),
          );
          void invalidateAssignees();
        },
      });
      if (saved) {
        addToast({
          message: `Accesso aggiornato per ${target.email}`,
          type: "success",
          duration: 3000,
        });
      }
      setModalMode({ kind: "closed" });
    } catch (err) {
      const msg = renameErrorMessage(err, "Impossibile salvare le modifiche.");
      setSubmitError(msg);
      addToast({ message: msg, type: "error", duration: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveConfirm = async () => {
    if (!pendingRemoval) return;
    setIsRemoving(true);
    try {
      await clientEmailsService.remove(clientId, pendingRemoval.id);
      setEmails((prev) => prev.filter((r) => r.id !== pendingRemoval.id));
      void invalidateAssignees();
      addToast({
        message: `Email rimossa: ${pendingRemoval.email}`,
        type: "success",
        duration: 3000,
      });
      setPendingRemoval(null);
    } catch (err) {
      addToast({
        message:
          err instanceof Error
            ? err.message
            : "Impossibile rimuovere l'email.",
        type: "error",
        duration: 5000,
      });
    } finally {
      setIsRemoving(false);
    }
  };

  const isLastEmail = emails.length === 1;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">
            Email di accesso
          </h4>
          <p className="text-xs text-gray-500 mt-1">
            Ogni indirizzo può richiedere un magic link per accedere. I
            ruoli &laquo;Cliente&raquo; e &laquo;Creator&raquo; determinano
            dove l'utente atterra dopo il login.
          </p>
          {teamIds.size > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              Il team ({teamIds.size} {teamIds.size === 1 ? "persona" : "persone"}) vede tutti i clienti.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            setSubmitError(null);
            setModalMode({ kind: "create" });
          }}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 shrink-0"
        >
          <PlusIcon className="w-4 h-4" />
          Aggiungi email
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <SpinnerIcon className="w-4 h-4" />
          Caricamento...
        </div>
      ) : loadError ? (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {loadError}
        </div>
      ) : emails.length === 0 ? (
        <p className="text-sm text-gray-500 italic">
          Nessuna email di accesso. Aggiungine una per abilitare il login.
        </p>
      ) : (
        <ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg">
          {emails.map((row) => (
            <li
              key={row.id}
              className="flex items-center justify-between px-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 truncate">
                    {row.name ?? row.email}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setSubmitError(null);
                      setModalMode({ kind: "edit", email: row });
                    }}
                    title="Modifica accesso"
                    className="rounded-full focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                  >
                    <RolePill role={row.role} />
                  </button>
                  {row.personId && teamIds.has(row.personId) && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-violet-50 text-violet-700">
                      Team
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                  {row.name && <span className="truncate">{row.email}</span>}
                  <span>Aggiunta {formatCreatedAt(row.createdAt)}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setPendingRemoval(row)}
                className="ml-3 p-2 text-gray-400 hover:text-red-600 transition-colors"
                title="Rimuovi email di accesso"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <EmailModal
        mode={modalMode}
        isSubmitting={isSubmitting}
        errorMessage={submitError}
        onClose={closeModal}
        onCreate={handleCreate}
        onSaveEdit={handleSaveEdit}
        people={people}
      />

      <ConfirmDialog
        isOpen={pendingRemoval !== null}
        onClose={() => {
          if (!isRemoving) setPendingRemoval(null);
        }}
        onConfirm={handleRemoveConfirm}
        title="Rimuovi email di accesso"
        message={
          isLastEmail
            ? `Questa è l'unica email associata al cliente. Rimuovendo ${pendingRemoval?.email ?? ""} verranno revocati tutti i magic link attivi e l'utente perderà l'accesso finché non viene configurato un altro metodo.`
            : `Rimuovere ${pendingRemoval?.email ?? ""}? Questo indirizzo non potrà più richiedere magic link per questo cliente.`
        }
        confirmText="Rimuovi"
        variant="danger"
        isLoading={isRemoving}
      />
    </div>
  );
};
